import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, computed, effect, inject, signal, viewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Subscription, map, mergeMap, of, tap } from 'rxjs';
import { RoundIconComponent } from '../round-icon/round-icon.component';
import { CartItemEntryComponent } from '../cart-item-entry/cart-item-entry.component';
import { AccentButtonComponent } from '../accent-button/accent-button.component';
import { HypermediaService } from '../../core/services/hypermedia.service';
import { OrderedItemService } from '../../core/services/ordering/ordered-item.service';
import { OrderService } from '../../core/services/order.service';
import { IconNameType } from '../icons/icon-name-type.enum';
import { OrderedItem } from '../../core/models/ordering/ordered-item.model';
import { UpsellType } from '../../core/models/ordering/upsell-type.enum';
import { OrderableItem } from '../../core/models/menu/orderable-item.model';
import { IngredientChoice } from '../../core/models/menu/ingredient-choice.model';
import { OrderableIngredient } from '../../core/models/menu/orderable-ingredient.model';
import { AccentButtonStyleType } from '../accent-button/button-types.enum';
import { ItemEditorService } from '../../core/services/item-editor.service';
import { OrderedIngredient } from '../../core/models/ordering/ordered-ingredient.model';
import { DeserializationUtils } from '../../core/utils/deserialization-utils';
import { ModalName, ModalService } from '../../core/services/modal.service';
import { LayoutService } from '../../core/services/layout.service';
import { EcommerceTriggeringArea } from '../../core/models/ecommerce/ecommerce-triggering-area.enum';
import { EcommerceTriggeringPoint } from '../../core/models/ecommerce/ecommerce-triggering-point.enum';
import { GTMService } from '../../core/services/gtm.service';
import { ConfigService } from '../../core/services/config.service';
import { StoreIdService } from '../../core/services/store-id.service';
import { AuditService } from '../../core/services/audit.service';
import { IconComponent } from "../icons/icon.component";
import { DeferService } from '../../core/services/ordering/defer.service';
import { DateUtils } from '../../core/utils/date-utils';
import { DeferChoice } from '../../core/models/defer-choice.enum';
import { SizePrice } from '../../core/models/menu/size-price.model';
import { A11yModule, FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';

@Component({
  selector: 'cwo-order-tree-notification-modal',
  standalone: true,
  imports: [
    AccentButtonComponent,
    RoundIconComponent,
    CartItemEntryComponent,
    TranslocoModule,
    RouterLink,
    IconComponent,
    A11yModule
  ],
  templateUrl: './order-tree-notification-modal.component.html',
  styleUrl: './order-tree-notification-modal.component.scss',
})
export class OrderTreeNotificationModalComponent implements OnInit, AfterViewInit, OnDestroy {
  iconNameType = IconNameType;
  accentButtonStyleType = AccentButtonStyleType;
  upsellBtnLabel = signal('');
  upsellDoneMessage = signal('');
  upsellSizeSubscription!: Subscription;
  upsellSubscription!: Subscription | null;
  autoCloseTimeout: number | undefined;
  storeIdService = inject(StoreIdService);
  private auditService = inject(AuditService);
  private deferService = inject(DeferService);
  private focusMonitor = inject(FocusMonitor);
  private timerPaused = false;

  httpClient = inject(HttpClient);
  hyperMediaService = inject(HypermediaService);
  translocoService = inject(TranslocoService);
  modalService = inject(ModalService);
  layoutService = inject(LayoutService);
  orderService = inject(OrderService);
  gtmService = inject(GTMService);
  order = computed(() => {
    return this.orderService.order();
  });
  orderedItemService = inject(OrderedItemService);
  orderedItem = computed(() => {
    return this.orderedItemService.selectedItem();
  });
  orderedItemWithoutUpsells = computed(() => {
    const copiedOrderedItem = new OrderedItem().deserialize(this.orderedItem());
    copiedOrderedItem.ingredientUpsell = undefined;
    copiedOrderedItem.sizeUpsell = undefined;
    return copiedOrderedItem;
  });
  itemEditorService = inject(ItemEditorService);
  storeConfig = inject(ConfigService).configState().store;

  hasNoticePeriodChanged = this.orderService.changedDefer();
  orderStillLoading = computed(() => { return this.orderService.loadingOrder(); });

  readonly orderTreeNotificationModal = viewChild.required<ElementRef>('orderTreeNotificationModal');

  modalState(){
    return this.modalService.stateForModal(ModalName.OrderTreeNotification);
  }

  constructor() {
    //handle timers when order is still loading with changes
    effect(() => {
      const loading = this.orderStillLoading();
      const noticeChanged = this.hasNoticePeriodChanged;

      if (loading || noticeChanged) { this.pauseTimer(); } 
      else { this.startTimer();}
    });
  }

  ngOnInit(): void {
    this.generateUpsellBtnLabel();
    if (!this.hasNoticePeriodChanged) {
      this.startTimer();
    } else {
      this.pauseTimer(); //clears previous
      this.autoCloseTimeout = setTimeout(() => {
        this.startTimer();
      }, 3000);
    }
  }

  ngAfterViewInit(): void {
    this.focusMonitor.monitor(this.orderTreeNotificationModal(), true).subscribe((origin: FocusOrigin) => {
      if (origin === 'keyboard') {
        this.timerPaused = true;
        this.pauseTimer();
      }
    });
  }

  ngOnDestroy(): void {
    this.pauseTimer();
    this.focusMonitor.stopMonitoring(this.orderTreeNotificationModal());
    this.upsellSizeSubscription?.unsubscribe();
    this.upsellSubscription?.unsubscribe();
    this.orderedItemService._selectedItem.set(null);
    this.orderService.changedDefer.set(false);
  }

  startTimer() {
    if (this.timerPaused) {
      return;
    }
    this.pauseTimer(); // clear existing
    this.autoCloseTimeout = setTimeout(() => {
      this.closeModal();
    }, 3000);
  }

  pauseTimer() {
    if (this.autoCloseTimeout !== undefined) {
      clearTimeout(this.autoCloseTimeout);
      this.autoCloseTimeout = undefined;
    }
  }

  closeModal() {
    this.modalService.closeModal(ModalName.OrderTreeNotification);
  }

  timeChangedMessage = computed(() => {
    //need to define those vars here to trigger computed
    this.orderStillLoading();
    const noticeChanged = this.hasNoticePeriodChanged;
    const orderDeferTime = this.order().deferTime;
  
    if (!noticeChanged && !this.modalState()?.removeItem) { return ''; }
      //if we come from deleting/clearing the defer from order is the correct, the timeslots wont run to be updated
      let time = orderDeferTime;
      if (!this.modalState()?.removeItem) {
        const timeslotFirst = this.deferService.timeSlots()[0];
        time = timeslotFirst ?? orderDeferTime ?? null;
      }

      let displayValue: string | null = null;

      if(time !== null) {
        const timeObj = DateUtils.calculateDate(time);
        const targetDate = new Date(time);
        const today = new Date();

        const isSameDay =
          targetDate.getFullYear() === today.getFullYear() &&
          targetDate.getMonth() === today.getMonth() &&
          targetDate.getDate() === today.getDate();

        displayValue = isSameDay
          ? timeObj.time
          : `${timeObj.date} at ${timeObj.time}`;
      }
    
    const orderTypeDisplayName = this.order().orderType ?? '';

    if (orderDeferTime === null && this.order().noticePeriod === 0) {
      return this.translocoService.translate(
        this.modalState()?.removeItem ? 'modals.order_notification.notice_period_removal' : 'modals.order_notification.notice_period',
        { time: DeferChoice.Asap, orderType: orderTypeDisplayName }
      );
    }

    return this.translocoService.translate(
      this.modalState()?.removeItem ? 'modals.order_notification.notice_period_removal' : 'modals.order_notification.notice_period',
      { time: displayValue, orderType: orderTypeDisplayName }
    );
  });


  private generateUpsellBtnLabel() {
    if (!this.orderedItem() || this.upsellDoneMessage()) {
      if(this.layoutService.mobileView()) {
        this.startTimer();
      }
      return;
    }

    if (this.orderedItem()?.sizeUpsell) {
      if ((this.orderedItem()?.sizeUpsell?.priceDiff ?? 0) > 0) {
        this.upsellBtnLabel.set(this.translocoService.translate(
          'modals.order_notification.make_it',
          {
            size: this.orderedItem()?.sizeUpsell?.sizeLabel,
            price: this.orderedItem()?.sizeUpsell?.priceDiff.toFixed(2),
          }
        ));
      } else {
        this.upsellBtnLabel.set(this.translocoService.translate(
          'modals.order_notification.make_it_for_free',
          {
            size: this.orderedItem()?.sizeUpsell?.sizeLabel,
          }
        ));
      }

      return;
    }
    if (this.orderedItem()?.ingredientUpsell) {
      if ((this.orderedItem()?.ingredientUpsell?.priceDiff ?? 0) > 0) {
        this.upsellBtnLabel.set(this.translocoService.translate(
          'modals.order_notification.add_ingredient',
          {
            ingredient: this.orderedItem()?.ingredientUpsell?.ingredient,
            price: this.orderedItem()?.ingredientUpsell?.priceDiff.toFixed(2),
          }
        ));
      } else {
        this.upsellBtnLabel.set(this.translocoService.translate(
          'modals.order_notification.add_ingredient_for_free',
          {
            ingredient: this.orderedItem()?.ingredientUpsell?.ingredient,
          }
        ));
      }
    }
  }

  findOrderableItemLink(): string {
    const orderableItemLink = this.hyperMediaService.findLink(
      this.orderedItem()!.links,
      'orderableItem'
    )
      ? this.hyperMediaService.findLink(this.orderedItem()!.links, 'orderableItem')?.href
      : undefined;
    const url = new URL(orderableItemLink!);
    return url.pathname + url.search;
  }

  upsellBtnClick(item: OrderedItem) {
    const orderableItemLink = this.findOrderableItemLink();
    const orderedItemBeforeUpsell = item.copy();
     this.auditService.createAudit(() => `Notification ${this.upsellBtnLabel()} button clicked`);
    this.upsellSubscription = this.httpClient
      .get(orderableItemLink!)
      .pipe(
        map((json) => new OrderableItem().deserialize(json)),
        mergeMap((orderableItem: OrderableItem) => {
          const orderedItem = DeserializationUtils.deserializeObj(this.orderedItem()?.serialize(), OrderedItem);
          orderedItem.isUpsell = true;

          if (item.sizeUpsell) {
            orderedItem.size = item.sizeUpsell.size;
            orderedItem.upsellType = UpsellType.SIZE_BASED;

            const newSize = orderableItem.sizePrices?.find((s: SizePrice) => s.size === item.sizeUpsell!.size);
            orderedItem.noticePeriod = newSize?.noticePeriod;
          }
          else {
            orderedItem.upsellType = UpsellType.INGREDIENT;
            const ingredientUpsell = item.ingredientUpsell;
            const insChoice = orderableItem.choices.filter(
              (choice: IngredientChoice) =>
                choice.ingredients
                  .map((ingredient) => ingredient.name)
                  .indexOf(ingredientUpsell!.ingredient) !== -1
            )[0];
            const insChoiceName = insChoice ? insChoice.name : undefined;

            const ingredientArrays: OrderableIngredient[][] =
              orderableItem?.choices?.map(
                (choice: IngredientChoice) => choice.ingredients
              ) ?? [];

            const allIngredients: OrderableIngredient[] = (
              [] as OrderableIngredient[]
            ).concat(...ingredientArrays);

            const insIngredient = allIngredients.find(
              (ingredient: OrderableIngredient) =>
                ingredient.name === ingredientUpsell?.ingredient
            );

            const orderedIngredient = OrderedIngredient.createFromOrderable(insIngredient!);
            const matchingChoice = insChoiceName
              ? orderedItem.choices.find(choice => choice.name === insChoiceName)
              : undefined;

            if (matchingChoice) {
              matchingChoice.ingredients.push(orderedIngredient);
            }

          }

          return of(orderedItem);
        }),
        mergeMap((upselledItem) => {

          return this.orderedItemService.updateItem(upselledItem).pipe(
            tap((orderedItem) => {
              if (item.sizeUpsell) {
                  this.auditService.createAudit(() => `${upselledItem.size} ${upselledItem.item} item has been updated in the order: ItemId : ${upselledItem.itemId}`);
              } else {
                this.auditService.createAudit(() => `Added ingedient ${this.upsellBtnLabel()} to ${upselledItem.item} `);
              }

              this.gtmService.pushRemoveFromCartEvent({
                items: [orderedItemBeforeUpsell],
                triggeringArea: EcommerceTriggeringArea.ORDER_TREE_NOTIFICATOIN,
                triggeringPoint: upselledItem.upsellType === UpsellType.SIZE_BASED ? EcommerceTriggeringPoint.SIZE_UPSELL_BUTTON : EcommerceTriggeringPoint.INGREDIENT_UPSELL_BUTTON,
              });
              this.gtmService.pushAddToCartEvent({
                items: [orderedItem],
                order: this.orderService.order(),
                triggeringArea: EcommerceTriggeringArea.ORDER_TREE_NOTIFICATOIN,
                triggeringPoint:  upselledItem.upsellType === UpsellType.SIZE_BASED ? EcommerceTriggeringPoint.SIZE_UPSELL_BUTTON : EcommerceTriggeringPoint.INGREDIENT_UPSELL_BUTTON,
              });
            }),
            map(() => upselledItem));
        })
      )
      .subscribe((value) => {
        this.upsellSubscription = null;
        if (value.upsellType === UpsellType.SIZE_BASED) {
          this.upsellDoneMessage.set(this.translocoService.translate("modals.order_notification.size_updated"));
        } else {
          this.upsellDoneMessage.set(this.translocoService.translate("modals.order_notification.ingredient_added"));
        }
        this.startTimer();
        this.upsellBtnLabel.set('');
        this.generateUpsellBtnLabel();
      });

  }

  private canCloseOnUserAction(event: Event): boolean {
    if (this.orderStillLoading() || this.timeChangedMessage()) return false;
    
    if (this.layoutService.mobileView()) {
      return !this.orderTreeNotificationModal().nativeElement.contains(event.target) &&
        (!!this.upsellDoneMessage() || !this.upsellBtnLabel());
    } else {
      return (!this.orderTreeNotificationModal().nativeElement.contains(event.target)) && event.type === 'click';
    }

  }

  @HostListener('mouseenter') onMouseEnter() {
    this.pauseTimer();
  }

  @HostListener('mouseleave') onMouseLeave() {
    this.startTimer();
  }

  @HostListener('document:click', ['$event'])
  @HostListener('document:scroll', ['$event'])
  handleEvent(event: Event) {
    if (this.canCloseOnUserAction(event)) {
      this.closeModal();
    }
  }
}
